import { devApiConfig } from '../../../../config/api.config';
import { Left, Right } from '../../../../shared/core/either';
import { Result } from '../../../../shared/core/result';
import { Map } from '../../../../shared/core/types/map';
import {
    accessTokenUrl,
    changePasswordUrl,
    loginUserUrl,
    Mode,
    resendEmailVerificationUrl,
    twoFADisableUrl,
    twoFAEnableUrl,
    twoFASetupUrl,
    twoFAStatusUrl,
    verifyUserEmailUrl,
    getActiveMode
} from '../../../../shared/helpers/endPoints';
// import { getStoreLocal } from '../../../../shared/helpers/utils';
import { APIResponse } from '../../../../shared/infrastructure/services/api.response';
import { BaseAxiosClient } from '../../../../shared/infrastructure/services/axios.client';
import { BaseAuthService } from '../auth/base_auth.service';
import BaseUserService from './base_user.service';
import qs from 'qs';

class UserService implements BaseUserService {
    private authService: BaseAuthService;
    private client: BaseAxiosClient;
    private storage: Storage;

    public constructor(authService: BaseAuthService, client: BaseAxiosClient, storage: Storage) {
        this.authService = authService;
        this.client = client;
        this.storage = storage;
    }

    public async loginService(email: string, password: string, mfa_code?: string): Promise<APIResponse<void>> {
        const modeType = this.storage.getItem('modeType') as Mode;

        let formData = new FormData();
        formData.append('username', email.toLowerCase().trim());
        formData.append('password', password);
        formData.append('scope', 'profile');
        formData.append('grant_type', 'password');
        if (mfa_code) {
            formData.append('mfa_code', mfa_code);
        }

        try {
            const response = await this.client.post({
                url: loginUserUrl(modeType),
                data: formData,
                auth: this.authService.basicToken(),
                headers: { 'Content-Type': 'multipart/form-data' }
            });

            const statusCode = response.status;

            console.log('[UserService] loginService status:', statusCode);
            console.log('[UserService] loginService response.data type:', typeof response.data);
            console.log(
                '[UserService] loginService response.data keys:',
                response?.data ? Object.keys(response.data) : null
            );

            if (statusCode >= 200 && statusCode < 300) {
                console.log('[UserService] Login success. Full response.data:', JSON.stringify(response.data));
                console.log('[UserService] Response has access_token:', !!response.data?.access_token);
                console.log('[UserService] Response has token:', !!response.data?.token);
                console.log('[UserService] Response.data structure - top level keys:', Object.keys(response.data));
                this.authService.setToken('auth-spay-user', response.data);
                console.log(
                    '[UserService] After setToken(auth-spay-user) localStorage spay-auth-token:',
                    localStorage.getItem('spay-auth-token')
                );
                return new Right(Result.ok<void>());
            }

            console.warn('[UserService] Login returned non-2xx status:', statusCode, response.data);
            this.authService.setToken('auth-spay-user', null);
            return new Left('Login failed. Unexpected response from server');
        } catch (error: any) {
            console.error('[UserService] loginService error object:', error);
            console.error('[UserService] loginService error response status:', error?.response?.status);
            console.error('[UserService] loginService error response data:', error?.response?.data);
            const customMsg = 'Something went wrong, Kindly check your network and try again later';
            const errorDescription = error?.response === undefined ? customMsg : error?.response.data.error_description;

            this.authService.setToken('auth-spay-user', null);
            return new Left(errorDescription);
        }
    }

    public async logoutService(): Promise<APIResponse<void>> {
        try {
            // Clear all auth tokens
            this.authService.removeToken('access-token');
            this.authService.removeToken('refresh-token');
            this.authService.removeToken('auth-spay-user');
            this.authService.removeToken('merchant-id');
            this.authService.removeToken('client-token');

            // Preserve modeType across the clear: it's what routes login and
            // 2FA requests to the LIVE vs TEST server. Wiping it makes the next
            // login's initial (pre-MFA) request silently fall back to TEST,
            // which can bypass 2FA enforcement for LIVE accounts.
            const modeType = localStorage.getItem('modeType');

            // Clear all local storage and session storage
            localStorage.clear();
            sessionStorage.clear();

            if (modeType) {
                localStorage.setItem('modeType', modeType);
            }

            return new Right(Result.ok<void>());
        } catch (error) {
            console.error('Error during logout:', error);
            return new Left('Failed to log out properly');
        }
    }

    public async createNewPasswordService(value: Map, urlToken: string): Promise<APIResponse<void>> {
        const modeType = this.storage.getItem('modeType') as Mode;
        const clienttoken = this.authService.getToken('client-token');

        try {
            const response = await this.client.post({
                url: changePasswordUrl(modeType),
                data: JSON.stringify({ ...value, token: urlToken }),
                headers: {
                    Authorization: `Bearer ${clienttoken}`,
                    'Content-Type': 'application/json',
                    'X-AUTH-PASSWORD': 'password'
                }
            });

            const statusCode = response.status;

            if (statusCode === 204) {
                return new Right(Result.ok<void>());
            }
        } catch (error: any) {
            const { message } = error.response.data.error;
            return new Left(message);
        }
    }

    public async verifyEmailService(token: string): Promise<APIResponse<void>> {
        const modeType = getActiveMode(this.storage.getItem('modeType') as Mode);
        let clienttoken: string;
        clienttoken = this.authService.getToken('client-token');

        try {
            if (!clienttoken || clienttoken === '' || clienttoken === undefined || clienttoken === null) {
                const firstResponse = await this.client.post({
                    url: accessTokenUrl(modeType),
                    data: qs.stringify({ grant_type: 'client_credentials' }),
                    auth: this.authService.basicToken(),
                    headers: { 'Content-Type': 'application/x-www-form-urlencoded' }
                });
                this.authService.setToken('client-token', firstResponse.data.access_token);
                clienttoken = firstResponse.data.access_token;
            }

            await this.client.post({
                url: verifyUserEmailUrl(modeType),
                data: { token },
                headers: {
                    Authorization: `Bearer ${clienttoken}`,
                    'Content-Type': 'application/json',
                    'X-AUTH-PASSWORD': 'password'
                }
            });

            return new Right(Result.ok<void>());
        } catch (error: any) {
            console.error('[UserService] verifyEmailService error:', error);
            const message =
                error?.response?.data?.error?.message ||
                error?.response?.data?.message ||
                error?.response?.data?.error_description ||
                (typeof error?.response?.data?.error === 'string' ? error.response.data.error : undefined) ||
                'Something went wrong';
            return new Left(message);
        }
    }

    public async validateTestUserPasswordService(password: string): Promise<APIResponse<void>> {
        const { SPAY_TEST_SERVER } = devApiConfig;
        const access_token = this.authService.accessToken();

        try {
            const response = await this.client.post({
                url: `${SPAY_TEST_SERVER}api/v1/users/validate-password`,
                data: { currentPassword: password },
                headers: {
                    Authorization: `Bearer ${access_token}`,
                    'Content-Type': 'application/json'
                }
            });

            const status: number = response.status;

            if (status === 204) {
                return new Right(Result.ok<void>());
            }
        } catch (error: any) {
            const { message } = error.response.data.error;
            return new Left(message);
        }
    }

    public async resendVerificationLinkService(params: any): Promise<APIResponse<string>> {
        const modeType = getActiveMode(this.storage.getItem('modeType') as Mode);
        try {
            const payload: any = { ...params };
            if (!payload.email) {
                try {
                    payload.email = localStorage.getItem('registered_email') || '';
                } catch (e) {}
            }

            let clienttoken = this.authService.getToken('client-token');
            if (!clienttoken) {
                try {
                    const firstResponse = await this.client.post({
                        url: accessTokenUrl(modeType),
                        data: qs.stringify({ grant_type: 'client_credentials' }),
                        auth: this.authService.basicToken(),
                        headers: { 'Content-Type': 'application/x-www-form-urlencoded' }
                    });
                    if (firstResponse?.data?.access_token) {
                        this.authService.setToken('client-token', firstResponse.data.access_token);
                        clienttoken = firstResponse.data.access_token;
                    }
                } catch (e) {
                    console.warn('[UserService] Could not fetch client token for resend:', e);
                }
            }

            const headers: any = {
                'Content-Type': 'application/json'
            };
            if (clienttoken) {
                headers['Authorization'] = `Bearer ${clienttoken}`;
                headers['X-AUTH-PASSWORD'] = 'password';
            }

            await this.client.post({
                url: resendEmailVerificationUrl(modeType),
                data: JSON.stringify(payload),
                headers: headers
            });
            const successMessage = 'Your email verification link has been resent';
            return new Right(Result.ok(successMessage));
        } catch (err: any) {
            const rawMessage =
                err?.response?.data?.error?.message ||
                err?.response?.data?.message ||
                err?.response?.data?.error_description ||
                (typeof err?.response?.data?.error === 'string' ? err.response.data.error : undefined);

            let errorMessage = rawMessage;
            if (rawMessage === 'token is required') {
                console.warn('[UserService] Backend /resend-verification-email requires a token from an existing email link. Backend update required to allow resending by email alone.');
                errorMessage = 'Your verification email was dispatched at signup. Please check your inbox and spam/junk folder. (If the email has not arrived, the server mail dispatch is currently being checked).';
            } else {
                console.error('[UserService] resendVerificationLinkService error:', err);
                if (!errorMessage) {
                    errorMessage = 'Failed to resend verification email';
                }
            }
            return new Left(errorMessage);
        }
    }

    public async get2FAStatusService(): Promise<APIResponse<{ is2faEnabled: boolean }>> {
        const modeType = this.storage.getItem('modeType') as Mode;
        const access_token = this.authService.accessToken();
        try {
            const response = await this.client.get({
                url: twoFAStatusUrl(modeType),
                headers: { Authorization: `Bearer ${access_token}` }
            });
            return new Right(Result.ok(response.data));
        } catch (error: any) {
            const msg = error?.response?.data?.message || 'Failed to get 2FA status';
            return new Left(msg);
        }
    }

    public async setup2FAService(): Promise<APIResponse<{ qrCodeUrl: string; secret: string }>> {
        const modeType = this.storage.getItem('modeType') as Mode;
        const access_token = this.authService.accessToken();
        try {
            const response = await this.client.get({
                url: twoFASetupUrl(modeType),
                headers: { Authorization: `Bearer ${access_token}` }
            });
            return new Right(Result.ok(response.data));
        } catch (error: any) {
            const msg = error?.response?.data?.message || 'Failed to initiate 2FA setup';
            return new Left(msg);
        }
    }

    public async enable2FAService(code: string): Promise<APIResponse<void>> {
        const modeType = this.storage.getItem('modeType') as Mode;
        const access_token = this.authService.accessToken();
        try {
            await this.client.post({
                url: `${twoFAEnableUrl(modeType)}?code=${code}`,
                data: {},
                headers: {
                    Authorization: `Bearer ${access_token}`,
                    'Content-Type': 'application/json'
                }
            });
            return new Right(Result.ok<void>());
        } catch (error: any) {
            const msg = error?.response?.data?.message || error?.response?.data || 'Invalid 2FA code. Try again.';
            return new Left(typeof msg === 'string' ? msg : 'Invalid 2FA code. Try again.');
        }
    }

    public async disable2FAService(): Promise<APIResponse<void>> {
        const modeType = this.storage.getItem('modeType') as Mode;
        const access_token = this.authService.accessToken();
        try {
            await this.client.post({
                url: twoFADisableUrl(modeType),
                data: {},
                headers: {
                    Authorization: `Bearer ${access_token}`,
                    'Content-Type': 'application/json'
                }
            });
            return new Right(Result.ok<void>());
        } catch (error: any) {
            const msg = error?.response?.data?.message || 'Failed to disable 2FA';
            return new Left(msg);
        }
    }
}

export default UserService;
